import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { MessageSquarePlus, StickyNote } from 'lucide-react'
import { cn } from '@/lib/utils'

export const ITEM_NOTE_MAX = 140
export const ORDER_NOTE_MAX = 280

/** Per-line kitchen note (special_instructions). */
export function ItemNotesPopover({
  productName,
  value,
  onChange,
}: {
  productName: string
  value: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const hasNote = value.trim().length > 0

  const handleSave = () => {
    onChange(draft.trim())
    setOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/70 text-muted-foreground transition-colors',
                'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                hasNote && 'border-primary/40 bg-primary/5 text-foreground',
              )}
              aria-label={hasNote ? `Edit note for ${productName}` : `Add note for ${productName}`}
            >
              <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {hasNote ? 'Edit item note' : 'Add item note'}
        </TooltipContent>
      </Tooltip>
      <PopoverContent align="end" sideOffset={6} className="w-[min(300px,calc(100vw-2rem))] p-3">
        <Label className="text-xs text-muted-foreground">Note for {productName}</Label>
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, ITEM_NOTE_MAX))}
          placeholder="e.g. no onions, extra spicy"
          rows={3}
          className="mt-1.5 text-sm"
        />
        <div className="mt-1 flex justify-end text-[10px] tabular-nums text-muted-foreground">
          {draft.length} / {ITEM_NOTE_MAX}
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={handleClear}
            disabled={!hasNote && draft.trim() === ''}
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" className="h-8 text-xs" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** Order-level note (orders.notes) — kitchen sees on every KOT slip. */
export function OrderNotePopover({
  value,
  onChange,
  totalNoteCount,
}: {
  value: string
  onChange: (v: string) => void
  totalNoteCount?: number
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const hasNote = value.trim().length > 0
  const count = totalNoteCount ?? (hasNote ? 1 : 0)

  const handleSave = () => {
    onChange(draft)
    setOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'relative inline-flex h-8 items-center gap-1.5 rounded-md border border-border/70 bg-background px-2.5 text-xs font-medium text-muted-foreground transition-colors',
                'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                count > 0 && 'border-primary/30 text-foreground',
              )}
              aria-label={count > 0 ? 'Edit notes' : 'Add order note'}
            >
              <StickyNote className="h-3.5 w-3.5" aria-hidden />
              <span>{count > 0 ? `Notes (${count})` : 'Add note'}</span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {count > 0
            ? `${count} note${count === 1 ? '' : 's'} attached`
            : 'Add a note for this whole order'}
        </TooltipContent>
      </Tooltip>
      <PopoverContent align="end" sideOffset={6} className="w-[min(340px,calc(100vw-2rem))] p-3">
        <Label className="text-xs text-muted-foreground">Order note</Label>
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, ORDER_NOTE_MAX))}
          placeholder="Anything the kitchen or staff should know about this whole order"
          rows={4}
          className="mt-1.5 text-sm"
        />
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
          <span>Printed on KOT slips</span>
          <span className="tabular-nums">
            {draft.length} / {ORDER_NOTE_MAX}
          </span>
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={handleClear}
            disabled={!hasNote && draft.trim() === ''}
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" className="h-8 text-xs" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
